import { UscgCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/UscgCompatibility/UscgCompatibilitiesBM';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';

const LOG_USCG_UTILS: boolean = false;
const compatibilityCache = new Map<string, boolean>();

// Pre-computed Maps for O(1) lookup
const interProductsMap: Map<string, boolean> = new Map();
const groupProductMap: Map<string, boolean> = new Map();
const interGroupsMap: Map<string, boolean> = new Map();

const initializeMaps = (uscgCompatibilities: UscgCompatibilitiesBM): void => {
    // TODO: PERFORMANCE - Module-level maps are never cleared. If different UscgCompatibilitiesBM instances are used across the application, stale data will persist. Consider clearing maps or using instance-based caching.
    if (interProductsMap.size > 0) return; // Already initialized

    // Pre-compute product compatibilities
    uscgCompatibilities.interProductsCompatibilities.forEach((c) => {
        interProductsMap.set(`${c.product1Id}_${c.product2Id}`, c.areCompatible);
        interProductsMap.set(`${c.product2Id}_${c.product1Id}`, c.areCompatible);
    });

    // Pre-compute group-product compatibilities
    uscgCompatibilities.groupProductCompatibilities.forEach((c) => {
        groupProductMap.set(`${c.uscgGroupNumber}_${c.productId}`, c.areCompatible);
    });

    // Pre-compute group compatibilities
    uscgCompatibilities.interGroupsCompatibilities.forEach((c) => {
        interGroupsMap.set(`${c.uscgGroupNumber1}_${c.uscgGroupNumber2}`, c.areCompatible);
        interGroupsMap.set(`${c.uscgGroupNumber2}_${c.uscgGroupNumber1}`, c.areCompatible);
    });
};

const getCacheKey = (id1: number, id2: number): string => (id1 < id2 ? `${id1}_${id2}` : `${id2}_${id1}`);

export const areCargoUscgCompatible = (uscgCompatibilities: UscgCompatibilitiesBM, cargo1: RotationCargo, cargo2: RotationCargo): boolean => {
    // Initialize maps on first call
    initializeMaps(uscgCompatibilities);

    // Check cache first
    const cacheKey = getCacheKey(cargo1.productId, cargo2.productId);
    const cachedResult = compatibilityCache.get(cacheKey);
    if (cachedResult !== undefined) return cachedResult;

    if (LOG_USCG_UTILS) {
        console.log(`Checking USCG compatibility between ${cargo1.product.tradeName} and ${cargo2.product.tradeName}`);
    }

    // Check direct compatibility rules first
    const directCompatibility = uscgCompatibilities.groupProductCompatibilities.find(
        (c) =>
            (c.uscgGroupNumber === cargo1.product.uscgGroup.number && c.productId === cargo2.productId) ||
            (c.uscgGroupNumber === cargo2.product.uscgGroup.number && c.productId === cargo1.productId)
    );

    if (directCompatibility) {
        const result = directCompatibility.areCompatible;
        compatibilityCache.set(cacheKey, result);
        return result;
    }

    // Check same group
    if (cargo1.product.uscgGroup.number === cargo2.product.uscgGroup.number) {
        compatibilityCache.set(cacheKey, true);
        return true;
    }

    // Check inter-products compatibility using productId
    const interProductKey = getCacheKey(cargo1.productId, cargo2.productId);
    const interProductsCompatibility = interProductsMap.get(interProductKey);
    if (interProductsCompatibility !== undefined) {
        compatibilityCache.set(cacheKey, interProductsCompatibility);
        return interProductsCompatibility;
    }

    // Check inter-groups compatibility
    const groupKey = `${cargo1.product.uscgGroup.number}_${cargo2.product.uscgGroup.number}`;
    const groupCompatibility = interGroupsMap.get(groupKey);
    if (groupCompatibility !== undefined) {
        compatibilityCache.set(cacheKey, groupCompatibility);
        return groupCompatibility;
    }

    // No compatibility found
    compatibilityCache.set(cacheKey, false);
    return false;
};
